<template>
  <n-card :title="title">
    <n-space vertical size="large">
      <n-space justify="end">
        <n-date-picker
          v-model:value="startDate"
          type="date"
          placeholder="Start Date"
          :actions="['clear']"
          @update:value="loadChartData"
        />
        <n-date-picker
          v-model:value="endDate"
          type="date"
          placeholder="End Date"
          :actions="['clear']"
          @update:value="loadChartData"
        />
        <n-button @click="resetDates">Reset Dates</n-button>
      </n-space>
      
      <div style="height: 400px">
        <Line
          v-if="chartData"
          :data="chartData"
          :options="chartOptions"
        />
      </div>
    </n-space>
  </n-card>
</template>
  
<script setup>
import { ref, onMounted, computed } from 'vue';
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { useChartStore } from '../stores/chartStore';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['monthly', 'yearly'].includes(value)
  },
  title: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['mounted']);

const chartStore = useChartStore();

const getDefaultDates = () => {
  const end = new Date();
  end.setDate(1);
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  start.setDate(1);
  
  return {
    start: start.getTime(),
    end: end.getTime()
  };
};

const { start, end } = getDefaultDates();
const startDate = ref(start);
const endDate = ref(end);

const chartData = computed(() => {
  const data = props.type === 'monthly' ? chartStore.monthlyData : chartStore.yearlyData;
  if (!data) return null;

  const bankValues = data.bank_accounts.values.map(v => typeof v === 'object' ? v.y : v);
  const savingsValues = data.savings.values.map(v => typeof v === 'object' ? v.y : v);

  return {
    labels: data.bank_accounts.dates,
    datasets: [
      {
        label: 'Bank Accounts',
        data: bankValues,
        fill: true,
        borderColor: '#4bc0c0',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        stack: 'stack1'
      },
      {
        label: 'Savings',
        data: savingsValues,
        fill: true,
        borderColor: '#ff9f40',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        tension: 0.1,
        stack: 'stack1'
      }
    ]
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const labels = [];
          const value = context.parsed.y.toFixed(2);
          const datasetLabel = context.dataset.label;
          
          labels.push(`${datasetLabel}: ${value}`);
          
          if (datasetLabel === 'Savings') {
            const total = context.chart.data.datasets.reduce((sum, dataset) => {
              return sum + dataset.data[context.dataIndex];
            }, 0);
            labels.push(`Total: ${total.toFixed(2)}`);
          }

          const comments = context.raw.comments;
          if (comments && comments.length > 0) {
            labels.push('Comments:');
            comments.forEach(comment => {
              labels.push(`- ${comment.text}`);
            });
          }

          return labels;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true
    },
    x: {
      stacked: true
    }
  }
};

const loadChartData = async () => {
  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  await chartStore.fetchChartData(
    props.type,
    formatDate(startDate.value),
    formatDate(endDate.value)
  );
};

const resetDates = () => {
  const { start, end } = getDefaultDates();
  startDate.value = start;
  endDate.value = end;
  loadChartData();
};

onMounted(() => {
  loadChartData().then(() => {
    emit('mounted');
  });
});
</script>
  
<style scoped>
.chart-container {
  height: 600px;
}
</style>